import {useCallback, useMemo, useState} from 'react';

import type {ParamCodec} from '@/shared/lib/use-param-state';

/**
 * Сортировка таблицы — один свод правил на весь кабинет.
 *
 * Раньше каждый список решал это сам: журнал имел свои компараторы, отчёт по
 * сотрудникам — свои, а устройства, ведомость и участники не сортировались вовсе.
 * Три реализации одного правила расходятся в мелочах, которые видит только
 * пользователь: в одной таблице «Ёлкин» стоит между «Евсеевым» и «Жуковым», в другой —
 * после «Яковлева»; в одной пустая клетка внизу, в другой она наверху и выглядит как
 * ответ.
 *
 * Здесь правил четыре, и они держатся везде:
 *
 * 1. **Пустое — всегда внизу, в ОБЕ стороны.** `null`, `undefined` и пустая строка —
 *    это «нет данных», а не «ноль» и не «начало алфавита». Перевернув столбец
 *    «Опоздание», человек ждёт пришедших вовремя, а не двадцать прочерков тех, у кого
 *    графика нет. Поэтому проверка стоит ДО умножения на знак.
 * 2. **Слова сравниваются по правилам языка.** `localeCompare`, а не коды символов: в
 *    Unicode «ё» стоит ПОСЛЕ «я», и наивная сортировка уносит каждого Ёлкина в самый
 *    низ состава.
 * 3. **Порядок устойчив.** Равные по ключу строки не переставляются от пересортировки:
 *    вторым ключом идёт исходный номер строки. Таблица, которая «дёргается» там, где
 *    ничего не менялось, читается как сломанная.
 * 4. **У чужого столбца есть осмысленная сторона.** Слова открываются по алфавиту,
 *    числа и признаки — от большего: нажимая «Опоздание», ищут опоздавших, а не
 *    отсутствие опозданий.
 */
export type SortDirection = 'asc' | 'desc';

export type SortState = {key: string; direction: SortDirection};

/**
 * Порядок таблицы как параметр адреса — `sort=time:desc`.
 *
 * Сортировка это часть ВОПРОСА («кто опаздывал больше всех»), а не жеста, поэтому она
 * ездит в ссылке и переживает перезагрузку. Умолчание в адрес не пишется.
 */
export function sortParam(fallback: SortState): ParamCodec<SortState> {
  return {
    parse: raw => {
      const [key, direction] = raw.split(':');
      return key
        ? {key, direction: direction === 'asc' ? 'asc' : 'desc'}
        : fallback;
    },
    format: value =>
      value.key === fallback.key && value.direction === fallback.direction
        ? undefined
        : `${value.key}:${value.direction}`,
  };
}

/** Что сравнивать. `null`/`undefined`/`''` означают «нечего». */
export type SortValue = string | number | boolean | null | undefined;

/** Как достать из строки значение каждого сортируемого столбца. */
export type SortAccessors<T> = Record<string, (row: T) => SortValue>;

function isBlank(value: SortValue): boolean {
  return value == null || value === '';
}

/** Сравнение двух значений одного столбца. Пустые сюда не доходят. */
function compareValues(a: SortValue, b: SortValue): number {
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
  return Number(a) - Number(b);
}

/**
 * Следующее состояние при нажатии на заголовок: свой столбец переворачивается, чужой
 * берётся с той стороны, с которой его обычно и открывают.
 *
 * Сторона выводится из ТИПА значения, а не назначается списком: слова читают по
 * алфавиту, числа и «да/нет» — от большего. Так новый столбец не требует помнить, в
 * какой таблице его завели.
 */
export function nextSortState<T>(
  current: SortState,
  key: string,
  accessors: SortAccessors<T>,
  sample?: T,
): SortState {
  if (current.key === key) {
    return {key, direction: current.direction === 'asc' ? 'desc' : 'asc'};
  }
  const probe = sample ? accessors[key]?.(sample) : undefined;
  const wordy = typeof probe === 'string';
  return {key, direction: wordy ? 'asc' : 'desc'};
}

/**
 * Копия списка в заданном порядке. Исходный массив принадлежит кэшу и неприкосновенен.
 *
 * `tiebreak` — чем разбивать равенство, когда исходного порядка недостаточно. По
 * умолчанию это НОМЕР строки: он делает сортировку устойчивой и ничего не выдумывает.
 * Но там, где исходный порядок сам по себе произволен (отчёт по компании приходит в
 * том порядке, в каком его собрал сервер), осмысленнее разбивать по имени — иначе
 * столбец из одних нулей выстраивает людей как попало.
 */
export function sortRows<T>(
  rows: readonly T[],
  state: SortState,
  accessors: SortAccessors<T>,
  tiebreak?: (row: T) => SortValue,
): T[] {
  const read = accessors[state.key];
  if (!read) {
    return [...rows];
  }
  const sign = state.direction === 'asc' ? 1 : -1;
  // Исходный номер строки — второй ключ, ради устойчивости (правило 3).
  return rows
    .map((row, index) => ({row, index, value: read(row)}))
    .sort((a, b) => {
      const blankA = isBlank(a.value);
      const blankB = isBlank(b.value);
      if (blankA !== blankB) {
        // Без знака намеренно: «нет данных» стоит внизу в обе стороны (правило 1).
        return blankA ? 1 : -1;
      }
      if (!blankA) {
        const primary = compareValues(a.value, b.value) * sign;
        if (primary !== 0) {
          return primary;
        }
      }
      if (tiebreak) {
        const secondary = compareValues(tiebreak(a.row), tiebreak(b.row));
        if (secondary !== 0) {
          return secondary;
        }
      }
      return a.index - b.index;
    })
    .map(entry => entry.row);
}

/**
 * Состояние сортировки таблицы: то, что отдаётся в `DataTable`, и уже отсортированные
 * строки.
 *
 * Строки принимает сам хук, а не только его `apply`, по двум причинам. Первая —
 * мемоизация: сортировать четыре тысячи строк на каждый рендер заметно и на мониторе.
 * Вторая — сторона, с которой открывается ЧУЖОЙ столбец: она выводится из типа
 * значения, а тип виден только на настоящей строке.
 */
export function useTableSort<T>(
  rows: readonly T[],
  accessors: SortAccessors<T>,
  /** Начальный порядок — или, в управляемом режиме, тот, что задан снаружи. */
  initial: SortState,
  /**
   * Вторичный ключ. Ссылка обязана быть СТАБИЛЬНОЙ — функция уровня модуля или
   * `useCallback`: новая стрелка на каждый рендер пересортировывает список впустую.
   */
  tiebreak?: (row: T) => SortValue,
  /**
   * Управляемый режим: порядок хранит вызывающий (обычно — адрес страницы, см.
   * `shared/lib/use-param-state`), а хук остаётся тем же самым для всех таблиц.
   * Без него порядок живёт внутри хука, как и раньше.
   */
  controlled?: {value: SortState; onChange: (next: SortState) => void},
): {
  state: SortState;
  /** Проп `sort` для `DataTable`: заголовки становятся нажимаемыми. */
  sort: {key: string; direction: SortDirection; onChange: (key: string) => void};
  /** Те же строки в выбранном порядке. */
  rows: T[];
  set: (next: SortState) => void;
} {
  const [own, setOwn] = useState<SortState>(initial);
  const state = controlled ? controlled.value : own;
  const setState = controlled ? controlled.onChange : setOwn;

  // Набор столбцов у списка неизменен, а литерал объекта — новый на каждый рендер;
  // без этого мемоизация ниже не держалась бы ни разу.
  const keys = Object.keys(accessors).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stable = useMemo(() => accessors, [keys]);

  const sample = rows[0];
  // Через текущее `state`, а не через обновляющую функцию: управляемый режим отдаёт
  // порядок наружу, и функционального `setState` там нет.
  const onChange = useCallback(
    (key: string) => setState(nextSortState(state, key, stable, sample)),
    [setState, state, stable, sample],
  );

  const sorted = useMemo(
    () => sortRows(rows, state, stable, tiebreak),
    [rows, state, stable, tiebreak],
  );

  return {
    state,
    sort: {key: state.key, direction: state.direction, onChange},
    rows: sorted,
    set: setState,
  };
}
